/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import Price from '@/components/AZCustomComponent/Price';
import { Rating } from '@/components/Rating/Rating';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { Skeleton, Text, View } from '@az/starc-ui';
import styles from './styles.module.scss';

export type ProductInformationProps = {
  averageRating: number | undefined;
  totalReviewCount: number | undefined;
};

const labelMap = {
  reviews: 'label_reviews',
  reviewsFor: 'label_reviews_for',
};

export function ProductInformation({ averageRating, totalReviewCount }: ProductInformationProps) {
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const { data: productDetails } = useProductDetails();
  const product = productDetails?.product;

  const { data: skuDetailsData } = useProductSkuDetails({
    skuIds: product?.itemId ? [product.itemId] : null,
    enabled: !!product?.itemId,
  });
  const skuDetails = skuDetailsData?.[0];

  const price = skuDetails?.skuPricingAndAvailability.corePrice
    ? skuDetails.skuPricingAndAvailability.retailPrice +
      skuDetails.skuPricingAndAvailability.corePrice
    : skuDetails?.skuPricingAndAvailability.retailPrice;

  return (
    <View>
      <View>
        <View>
          <Text className={styles.description} as="h1">
            {product?.itemDescription ? (
              `${labels.reviewsFor} ${product.itemDescription}`
            ) : (
              <Skeleton className={styles.descriptionSkeleton} borderRadius="small" />
            )}
          </Text>
        </View>

        <View className={styles.identifiersWrapper}>
          {!!product?.partNumber && !!product.itemId ? (
            <View
              direction="row"
              align="center"
              divided
              className={styles.identifiers}
              attributes={{ style: { '--st-color-disabled': '#000' } }}
            >
              <Text as="span">Part #{product.partNumber}</Text>
              <Text as="span">SKU #{product.itemId}</Text>
            </View>
          ) : (
            <Skeleton width="200px" className={styles.identifiersSkeleton} borderRadius="small" />
          )}
        </View>

        <View className={styles.pricingAndRating}>
          <View className={styles.pricingContainer}>
            {price ? (
              <Price
                dealPrice={price.toFixed(2).split('.')}
                priceType="primary"
                priceIconClassName={styles.superscript}
                priceClassName={styles.basePrice}
                centsClassName={styles.superscript}
              />
            ) : (
              <Skeleton width="100px" height="100%" borderRadius="small" />
            )}
          </View>
          <>
            <View className={styles.starRating}>
              <Rating value={averageRating ?? 0} showStarFractions />
            </View>
            <View className={styles.ratingDescription}>
              {averageRating !== undefined && totalReviewCount !== undefined ? (
                <Text as="span">
                  <strong aria-hidden>{averageRating.toFixed(1)}</strong>
                  {` (${totalReviewCount.toLocaleString(locale)} ${labels.reviews})`}
                </Text>
              ) : (
                <Skeleton width="50px" className={styles.ratingSkeleton} />
              )}
            </View>
          </>
        </View>
      </View>
    </View>
  );
}
